import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Loader2 } from 'lucide-react';
import { CARD_KIND_LABELS, type ApprovalCardView } from '@evi/shared';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { getDisplayErrorMessage } from '@/lib/api';
import { ageLabel, approveCard, cardMoneySentence, cardWho, declineCard, declineSentence, fetchApproval, fetchApprovals, resultLine, waitingLabel } from '@/lib/approvals';
import styles from './ApprovalsPage.module.css';

/**
 * Today, Approvals (Warren, 29 Oct 2026, item 2): one card per action, with what, who, how much and why, and Approve
 * and Decline running the same audited path the scripts run. Built for a phone first. Approve opens a confirm sheet that
 * repeats the money sentence and re-reads the card (the Api says when the facts changed since). Whether the caller may
 * decide a card is the Api's answer on the card (can_decide and its reason), never the persona.
 */
const KEY = ['approvals'];

function Card({ card, focus }: { card: ApprovalCardView; focus: boolean }) {
  const client = useQueryClient();
  const [sheet, setSheet] = useState<'approve' | 'decline' | null>(null), [note, setNote] = useState('');
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [done, setDone] = useState<string | null>(null);
  const detail = useQuery({ queryKey: [...KEY, card.id], queryFn: () => fetchApproval(card.id), enabled: sheet === 'approve', staleTime: 0, retry: false });
  const now = new Date();
  async function act(kind: 'approve' | 'decline') {
    setBusy(true); setError('');
    try {
      const r = kind === 'approve' ? await approveCard(card.id, card.review_hash) : await declineCard(card.id, note.trim());
      setDone(resultLine(r.card)); setSheet(null);
      await client.invalidateQueries({ queryKey: KEY });
    } catch (err) {
      setError(await getDisplayErrorMessage(err, kind === 'approve' ? 'It was not approved.' : 'It was not declined.'));
      await client.invalidateQueries({ queryKey: KEY });
    } finally { setBusy(false); }
  }
  const who = cardWho(card);
  return <article className={`${styles.card} ${focus ? styles.focus : ''}`} aria-label={card.title}>
    <p className={styles.kind}>{CARD_KIND_LABELS[card.kind]}</p>
    <h3 className={styles.what}>{card.title}</h3>
    {who && <p className={styles.row}><span className={styles.label}>Who</span><span className={styles.value}>{who}</span></p>}
    <p className={styles.row}><span className={styles.label}>How much</span><span className={styles.value}>{cardMoneySentence(card)}</span></p>
    {card.partial && <p className={styles.row}><span className={styles.label}>Already done</span><span className={styles.value}>{card.partial.sentence}</span></p>}
    <p className={styles.row}><span className={styles.label}>Why</span><span className={styles.value}>{card.why.text}</span></p>
    <p className={styles.meta}>Proposed {card.proposed_by.kind === 'system' ? 'by the platform' : card.proposed_by.name ? `by ${card.proposed_by.name}` : ''} {ageLabel(card.created_at, now)}
      {card.status === 'pending' ? `, ${waitingLabel(card, now)}` : ''}.</p>
    {done ? <p className={styles.result} role="status">{done}</p>
      : card.status !== 'pending' ? <p className={styles.result}>{resultLine(card)}</p>
        : card.can_decide ? <>
          {!sheet && !card.can_approve && <p className={styles.cannot}>This cannot be approved as it stands: a person handles it by hand, then declines the card.</p>}
          {!sheet && <div className={styles.actions}>
            {card.can_approve && <button type="button" className={styles.approve} onClick={() => setSheet('approve')} disabled={busy}>Approve</button>}
            <button type="button" className={styles.decline} onClick={() => setSheet('decline')} disabled={busy}>Decline</button>
          </div>}
          {sheet === 'approve' && <div className={styles.sheet} role="dialog" aria-label="Confirm the approval">
            <p className={styles.sheetText}>{cardMoneySentence(card)}</p>
            {detail.isLoading ? <p className={styles.meta}><Loader2 size={13} /> Checking the facts again.</p>
              : detail.data?.now?.changed ? <p className={styles.changed}>The facts changed since this card was made. Approving now replaces it with a new card; nothing moves.</p>
                : null}
            <div className={styles.actions}>
              <button type="button" className={styles.confirm} onClick={() => void act('approve')} disabled={busy}>{busy ? 'Approving' : 'Approve'}</button>
              <button type="button" className={styles.cancel} onClick={() => setSheet(null)} disabled={busy}>Cancel</button>
            </div>
          </div>}
          {sheet === 'decline' && <div className={styles.sheet} role="dialog" aria-label="Decline the card">
            <label className={styles.sheetText} htmlFor={`note-${card.id}`}>{declineSentence(card)} A note for the record (optional):</label>
            <textarea id={`note-${card.id}`} className={styles.note} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
            <div className={styles.actions}>
              <button type="button" className={styles.confirm} onClick={() => void act('decline')} disabled={busy}>{busy ? 'Declining' : 'Decline'}</button>
              <button type="button" className={styles.cancel} onClick={() => setSheet(null)} disabled={busy}>Cancel</button>
            </div>
          </div>}
        </> : <p className={styles.cannot}>{card.cannot_decide_reason}</p>}
    {error && <p className={styles.error} role="alert">{error}</p>}
  </article>;
}

export function ApprovalsPage({ focusId }: { focusId: string | null }) {
  useRecentRoute('Approvals');
  const q = useQuery({ queryKey: KEY, queryFn: fetchApprovals, staleTime: 15_000, retry: false });
  const waiting = q.data?.waiting ?? [];
  const ordered = focusId ? [...waiting.filter((c) => c.id === focusId), ...waiting.filter((c) => c.id !== focusId)] : waiting;
  return <div className={styles.page}>
    <div className={styles.header}><BadgeCheck size={20} aria-hidden /><h1 className={styles.title}>Approvals</h1></div>
    <p className={styles.lede}>What the platform would not do on its own. Each card says what, who, how much and why. Approve runs it now, the way the
      reviewed scripts do; Decline moves nothing. A card nobody decides expires after 7 days. A card for something that stopped part way says what
      already happened: Approve finishes it, Decline gives it back, and it waits until someone does one or the other.</p>
    {q.isLoading ? <p className={styles.empty}><Loader2 size={13} /> Loading.</p>
      : q.isError ? <p className={styles.error} role="alert">Couldn&rsquo;t load the cards. Refresh to retry.</p>
        : !q.data?.holds.length ? <p className={styles.empty}>You do not hold an approver type, so no cards come to you. The settings owner names approvers in
          Settings, Standing authority.</p>
          : <>
            <h2 className={styles.section}>Waiting ({waiting.length})</h2>
            {ordered.length ? ordered.map((c) => <Card key={c.id} card={c} focus={c.id === focusId} />) : <p className={styles.empty}>Nothing is waiting for you.</p>}
            {!!q.data.decided.length && <>
              <h2 className={styles.section}>Recently decided</h2>
              {q.data.decided.slice(0, 20).map((c) => <Card key={c.id} card={c} focus={c.id === focusId} />)}
            </>}
          </>}
  </div>;
}
